import { CashFlow, ExpenseGroup } from '@api/CashFlows/CashFlow';
import { DailySales } from '@api/IncomeStatement/DailySales';
import { countsInIncomeStatement, expenseGroupOf, isActiveFlow, isExpenseFlow, UNCATEGORIZED } from '@utils/CashFlows';
import { isMonthlyGroup, periodOf } from '@utils/Expenses';
import { toDateSafe } from '@utils/Date';

// ============================================================================
// ESTADO DE RESULTADOS — puro y con pruebas (plan de Gastos §5.3, F5).
//
//   Ventas netas            Σ netSales de `daily-sales` (subtotal − promoción − puntos; sin propina)
// + Ingresos por envío      Σ shipping
// − Insumos                 compras en la calle (gastos del grupo Insumos) + pedidos al CEDIS pagados
// = Utilidad bruta          y su margen
// − Nómina · Gastos fijos · Gastos variables · Mantenimiento · Gasto operativo · Sin categoría
// = Utilidad de operación   y su margen
//   Informativo: propinas cobradas vs entregadas, ajustes de caja, lo estimado.
//
// Reglas:
// - **A día cerrado**: el día de hoy no entra (la regla de la casa para comparar).
// - Un gasto del MES (fijo o variable con `period`) cuenta en el mes al que
//   pertenece, no en el día en que se pagó: la luz de septiembre pagada en octubre
//   es de septiembre. Lo demás, por la fecha del gasto.
// - Un fijo por confirmar cuenta con su monto estimado y se marca "estimado".
// - Lo que no es gasto del negocio (propinas entregadas, ajustes) no resta.
// - "Sin categoría" (lo capturado antes de F2) SÍ resta, en su propio renglón: sin
//   él, los meses viejos darían una utilidad que no existió.
// ============================================================================

/** `yyyy-MM-dd` en hora local (el admin corre en México). */
export const dayKey = (date: Date): string =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/** Los meses `yyyy-MM` de `from` a `to`, incluidos. */
export const monthsBetween = (from: string, to: string): string[] => {
    const [fy, fm] = from.split('-').map(Number);
    const [ty, tm] = to.split('-').map(Number);
    const months: string[] = [];
    for (let y = fy, m = fm; y < ty || (y === ty && m <= tm); m === 12 ? ((m = 1), (y += 1)) : (m += 1)) {
        months.push(`${y}-${String(m).padStart(2, '0')}`);
        if (months.length > 120) break;
    }
    return months;
};

export interface StatementRange {
    /** Los meses que cubre (para los gastos del mes). */
    periods: string[];
    /** Primer y último día que entran, `yyyy-MM-dd`, ambos incluidos. */
    startDay: string;
    endDay: string;
    /** El último mes todavía no termina: los días van hasta ayer. */
    partial: boolean;
    /** Nada que mostrar (el periodo empieza hoy o en el futuro). */
    empty: boolean;
}

/** El rango a día cerrado: del día 1 del primer mes al último del último, o hasta AYER. */
export const buildStatementRange = (from: string, to: string, now: Date = new Date()): StatementRange => {
    const periods = monthsBetween(from, to);
    const [ty, tm] = to.split('-').map(Number);
    const lastOfTo = new Date(ty, tm, 0);
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    const end = lastOfTo < yesterday ? lastOfTo : yesterday;
    const startDay = `${from}-01`;
    const endDay = dayKey(end);
    return { periods, startDay, endDay, partial: lastOfTo >= yesterday, empty: endDay < startDay };
};

/** El mismo número de meses, justo antes (para comparar). */
export const previousPeriods = (from: string, to: string): { from: string; to: string } => {
    const count = monthsBetween(from, to).length;
    const [fy, fm] = from.split('-').map(Number);
    const start = new Date(fy, fm - 1, 1);
    return { from: periodOf(start, -count), to: periodOf(start, -1) };
};

/**
 * El periodo anterior recortado igual que el actual: si el mes en curso va al día
 * 26, se compara contra los primeros 26 días del mes anterior. Si no, un mes a
 * medias siempre saldría "peor" que uno completo.
 */
export const buildPreviousRange = (current: StatementRange, from: string, to: string): StatementRange => {
    const previous = previousPeriods(from, to);
    const full = buildStatementRange(previous.from, previous.to, new Date(9999, 0, 1));
    if (!current.partial || current.empty) return full;
    const endDate = new Date(Number(current.endDay.slice(0, 4)), Number(current.endDay.slice(5, 7)) - 1, Number(current.endDay.slice(8, 10)));
    const shifted = new Date(endDate.getFullYear(), endDate.getMonth() - current.periods.length, 1);
    const lastOfShifted = new Date(shifted.getFullYear(), shifted.getMonth() + 1, 0);
    const day = Math.min(endDate.getDate(), lastOfShifted.getDate());
    const endDay = dayKey(new Date(shifted.getFullYear(), shifted.getMonth(), day));
    return { ...full, endDay: endDay < full.endDay ? endDay : full.endDay, partial: true };
};

/** Lo que pasó por la caja de un pedido al CEDIS que entra al ER (ya filtrado: pagado y no cancelado). */
export interface CedisPurchase {
    branchOfficeId?: string;
    total?: number;
    /** `yyyy-MM-dd` del pedido. Sin él, se asume que ya viene del periodo. */
    day?: string;
}

export interface IncomeStatementInput {
    dailySales: DailySales[];
    flows: CashFlow[];
    cedisPurchases: CedisPurchase[];
    range: StatementRange;
}

export interface IncomeStatement {
    orders: number;
    subtotal: number;
    promotion: number;
    points: number;
    netSales: number;
    shipping: number;
    /** Ventas netas + envío: la base de los márgenes. */
    income: number;
    suppliesStreet: number;
    suppliesCedis: number;
    supplies: number;
    /**
     * Merma registrada en el periodo (plan de inventario F3), de `daily-sales`. **Informativo,
     * nunca resta**: los insumos ya restaron al comprarse; restarla otra vez la contaría doble.
     */
    shrinkage: number;
    /** Merma ÷ insumos comprados (CEDIS + calle). `null` sin compras. */
    shrinkageShare: number | null;
    /** Diferencia de conteos valuada (no explicada); negativo = faltante. Informativo. */
    countDifference: number;
    grossProfit: number;
    /** Fracción (0.42 = 42 %). `null` sin ingresos. */
    grossMargin: number | null;
    payroll: number;
    fixed: number;
    variable: number;
    maintenance: number;
    operating: number;
    uncategorized: number;
    /** Todo lo que se resta después de la utilidad bruta. */
    operatingExpenses: number;
    operatingProfit: number;
    operatingMargin: number | null;
    /** Fijos por confirmar incluidos arriba con su monto estimado, por renglón. */
    estimated: number;
    estimatedByGroup: Record<string, number>;
    tipsCollected: number;
    tipsDelivered: number;
    /** Ajustes de caja y otros "fuera del ER": informativo. */
    adjustments: number;
    /** Días con `daily-sales` (por sucursal): para avisar si falta el agregado. */
    salesDays: number;
}

const cents = (value: number): number => Math.round(value * 100) / 100;
const num = (value: unknown): number => Number(value) || 0;
const TIPS_CATEGORY_ID = 'outside-tips';

/** ¿El gasto es de este periodo? Los del mes por su `period`; lo demás por su fecha. */
export const flowInRange = (flow: CashFlow, range: StatementRange): boolean => {
    const group = expenseGroupOf(flow);
    if (isMonthlyGroup(group) && flow.period) return range.periods.includes(flow.period);
    const date = toDateSafe(flow.date);
    if (!date) return false;
    const day = dayKey(date);
    return day >= range.startDay && day <= range.endDay;
};

export const buildIncomeStatement = ({ dailySales, flows, cedisPurchases, range }: IncomeStatementInput): IncomeStatement => {
    const sales = dailySales.filter((row) => row.day >= range.startDay && row.day <= range.endDay);
    const sum = (pick: (row: DailySales) => number) => sales.reduce((total, row) => total + pick(row), 0);
    const byGroup: Record<string, number> = {};
    const estimatedByGroup: Record<string, number> = {};
    let tipsDelivered = 0;
    let adjustments = 0;

    flows.filter((flow) => isExpenseFlow(flow) && isActiveFlow(flow) && flowInRange(flow, range)).forEach((flow) => {
        const amount = num(flow.amount);
        if (!countsInIncomeStatement(flow)) {
            if (flow.categoryId === TIPS_CATEGORY_ID) tipsDelivered += amount;
            else adjustments += amount;
            return;
        }
        const group = expenseGroupOf(flow) || UNCATEGORIZED;
        byGroup[group] = (byGroup[group] || 0) + amount;
        if (flow.pendingConfirmation) estimatedByGroup[group] = (estimatedByGroup[group] || 0) + amount;
    });

    const g = (group: string) => cents(byGroup[group] || 0);
    const netSales = cents(sum((row) => num(row.netSales)));
    const shipping = cents(sum((row) => num(row.shipping)));
    const income = cents(netSales + shipping);
    const suppliesStreet = g(ExpenseGroup.Supplies);
    const suppliesCedis = cents(
        cedisPurchases
            .filter((purchase) => !purchase.day || (purchase.day >= range.startDay && purchase.day <= range.endDay))
            .reduce((total, purchase) => total + num(purchase.total), 0)
    );
    const supplies = cents(suppliesStreet + suppliesCedis);
    // Merma y diferencia de conteos (F3): dos números que el aseo deja en `daily-sales`.
    // Van INFORMATIVOS: no entran en `grossProfit` ni en nada que reste.
    const shrinkage = cents(sum((row) => num(row.shrinkage)));
    const countDifference = cents(sum((row) => num(row.countDifference)));
    const grossProfit = cents(income - supplies);
    const payroll = g(ExpenseGroup.Payroll);
    const fixed = g(ExpenseGroup.Fixed);
    const variable = g(ExpenseGroup.Variable);
    const maintenance = g(ExpenseGroup.Maintenance);
    const operating = g(ExpenseGroup.Operating);
    const uncategorized = g(UNCATEGORIZED);
    const operatingExpenses = cents(payroll + fixed + variable + maintenance + operating + uncategorized);
    const operatingProfit = cents(grossProfit - operatingExpenses);
    Object.keys(estimatedByGroup).forEach((group) => (estimatedByGroup[group] = cents(estimatedByGroup[group])));

    return {
        orders: sum((row) => num(row.orders)),
        subtotal: cents(sum((row) => num(row.subtotal))),
        promotion: cents(sum((row) => num(row.promotion))),
        points: cents(sum((row) => num(row.points))),
        netSales,
        shipping,
        income,
        suppliesStreet,
        suppliesCedis,
        supplies,
        shrinkage,
        shrinkageShare: supplies ? shrinkage / supplies : null,
        countDifference,
        grossProfit,
        grossMargin: income ? grossProfit / income : null,
        payroll,
        fixed,
        variable,
        maintenance,
        operating,
        uncategorized,
        operatingExpenses,
        operatingProfit,
        operatingMargin: income ? operatingProfit / income : null,
        estimated: cents(Object.values(estimatedByGroup).reduce((total, value) => total + value, 0)),
        estimatedByGroup,
        tipsCollected: cents(sum((row) => num(row.tips))),
        tipsDelivered: cents(tipsDelivered),
        adjustments: cents(adjustments),
        salesDays: sales.length
    };
};

/** El mismo cálculo, una columna por sucursal (para la tabla "Por sucursal"). */
export const buildIncomeStatementByBranch = (
    input: IncomeStatementInput,
    branchOfficeIds: string[]
): Record<string, IncomeStatement> =>
    Object.fromEntries(
        branchOfficeIds.map((branchOfficeId) => [
            branchOfficeId,
            buildIncomeStatement({
                range: input.range,
                dailySales: input.dailySales.filter((row) => row.branchOfficeId === branchOfficeId),
                flows: input.flows.filter((flow) => flow.branchOfficeId === branchOfficeId),
                cedisPurchases: input.cedisPurchases.filter((purchase) => purchase.branchOfficeId === branchOfficeId)
            })
        ])
    );

/** Los renglones en orden, para pintar la tabla y armar el Excel con la MISMA lista. */
export interface StatementRow {
    key: keyof IncomeStatement;
    label: string;
    /** Suma (+), resta (−) o resultado (=). */
    kind: 'plus' | 'minus' | 'result' | 'info';
    /** Grupo del que sale el "estimado". */
    group?: string;
    /** El margen que va junto al resultado. */
    margin?: keyof IncomeStatement;
    /** Un renglón `info` con su propio porcentaje (la merma como % de los insumos). */
    share?: keyof IncomeStatement;
    shareLabel?: string;
    indent?: boolean;
}

export const STATEMENT_ROWS: StatementRow[] = [
    { key: 'netSales', label: 'Ventas netas', kind: 'plus' },
    { key: 'shipping', label: 'Ingresos por envío', kind: 'plus' },
    { key: 'supplies', label: 'Insumos', kind: 'minus', group: ExpenseGroup.Supplies },
    { key: 'suppliesStreet', label: 'Compras en la calle', kind: 'info', indent: true },
    { key: 'suppliesCedis', label: 'Pedidos al CEDIS', kind: 'info', indent: true },
    // Plan de inventario F3: informativos, ya incluidos en Insumos (no restan dos veces)
    { key: 'shrinkage', label: 'Merma registrada (ya incluida en Insumos)', kind: 'info', indent: true, share: 'shrinkageShare', shareLabel: 'de los insumos' },
    { key: 'countDifference', label: 'Diferencia de conteos (no explicada; − = faltante)', kind: 'info', indent: true },
    { key: 'grossProfit', label: 'Utilidad bruta', kind: 'result', margin: 'grossMargin' },
    { key: 'payroll', label: 'Nómina', kind: 'minus', group: ExpenseGroup.Payroll },
    { key: 'fixed', label: 'Gastos fijos', kind: 'minus', group: ExpenseGroup.Fixed },
    { key: 'variable', label: 'Gastos variables', kind: 'minus', group: ExpenseGroup.Variable },
    { key: 'maintenance', label: 'Mantenimiento', kind: 'minus', group: ExpenseGroup.Maintenance },
    { key: 'operating', label: 'Gasto operativo', kind: 'minus', group: ExpenseGroup.Operating },
    { key: 'uncategorized', label: 'Gastos sin categoría (antes de categorizar)', kind: 'minus', group: UNCATEGORIZED },
    { key: 'operatingProfit', label: 'Utilidad de operación', kind: 'result', margin: 'operatingMargin' }
];

/** Variación contra el periodo anterior; `null` si antes era 0 (no hay porcentaje honesto). */
export const variation = (current: number, previous: number): number | null =>
    previous ? (current - previous) / Math.abs(previous) : null;
